import type { ThemeState } from '@atlaskit/tokens';
import { proxy } from 'valtio';

import type { Theme } from '@canvas/constants/ui/theme';

export const currentThemeState: { theme: Theme } = proxy({
  theme: 'light',
});

/**
 * A host-requested theme that has been deferred while a `forceColorMode`
 * override is active. Mirrors the input type accepted by
 * `@atlaskit/tokens`'s `setGlobalTheme`, so it can be replayed verbatim by
 * the consumer that releases the override.
 */
export type DeferredHostTheme = Partial<ThemeState>;

interface ThemeOverrideState {
  /**
   * When set, host-driven theme updates (e.g. from the Confluence shell) are
   * deferred rather than applied immediately. Slides presenter mode uses this
   * to force dark mode for the duration of a presentation while still
   * respecting the user's intent on exit.
   */
  forceColorMode: 'dark' | 'light' | null;

  /**
   * The most recent host-requested theme that was deferred while
   * `forceColorMode` was active. Consumed and cleared by the system that
   * activated the override (typically on exit) so the deferred update can be
   * applied.
   */
  pendingHostTheme: DeferredHostTheme | null;
}

/**
 * Allows a feature (currently slides presenter mode) to temporarily force a
 * specific color mode and defer host-driven theme updates until the override
 * is released. Reads of `currentThemeState.theme` continue to reflect the
 * currently-applied theme.
 */
export const themeOverrideState: ThemeOverrideState = proxy<ThemeOverrideState>({
  forceColorMode: null,
  pendingHostTheme: null,
});
